import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { USE_MOCK, surveyApi } from '@/api/client';
import { MOCK_TEMPLATES } from '@/data/mockTemplates';
import type { TemplateItem } from '@/api/types';

function adaptMockTemplate(t: typeof MOCK_TEMPLATES[number]): TemplateItem {
  return {
    id: String(t.id),
    name: t.nameKey,
    description: t.desc,
    questionCount: t.questions,
    category: t.category,
    createdBy: t.createdBy,
    usesCount: t.uses,
    createdAt: t.startDate ?? '',
    expiresAt: t.expirationDate ?? undefined,
    isTemplate: true,
  };
}

export function useTemplates() {
  return useQuery<TemplateItem[]>({
    queryKey: ['templates'],
    queryFn: async () => {
      if (USE_MOCK) return MOCK_TEMPLATES.map(adaptMockTemplate);
      const data = await surveyApi.getTemplates();
      return (data.items ?? []).map(s => ({
        id: s.id,
        name: s.name,
        description: s.description,
        questionCount: s.questions?.length ?? 0,
        category: s.category ? s.category.charAt(0).toUpperCase() + s.category.slice(1) : undefined,
        createdBy: s.createdBy,
        usesCount: 0,
        createdAt: s.createdAt,
        expiresAt: s.expiresAt,
        isTemplate: true,
      }));
    },
  });
}

export function useDuplicateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] duplicateTemplate', id);
        return Promise.resolve({ id: crypto.randomUUID(), name: 'Copia', status: 'draft', version: 1, createdBy: 'demo', createdAt: new Date().toISOString() });
      }
      return surveyApi.duplicateTemplate(id);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['templates'] });
      void qc.invalidateQueries({ queryKey: ['surveys'] });
    },
  });
}

export function useDeleteTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => {
      if (USE_MOCK) {
        if (import.meta.env.DEV) console.log('[mock] deleteTemplate', id);
        return Promise.resolve();
      }
      return surveyApi.deleteTemplate(id);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['templates'] }); },
  });
}
